import { safeDeleteFromLocalStorage, safeGetFromLocalStorage, safeSetInLocalStorage } from '../../helpers/window';
import { amplitudeModule } from './AmplitudeModule';
import type { PrescriptionStartedPayload } from './types';
import type { PrescriptionCompletedPayload } from './types';
import type { PrescriptionCompletedProtocol } from './types';

const PRESCRIPTION_STARTED_KEY = (prescriptionId: number) => `amplitude_prescription_started_${prescriptionId}`;

/**
 * Persiste o payload do evento prescription_started no localStorage.
 * Usado para garantir que os dados do funil não sejam perdidos em caso de reload.
 */
export function savePrescriptionStartedPayload(prescriptionId: number, payload: PrescriptionStartedPayload): void {
  if (!amplitudeModule.isInitialized()) return;
  safeSetInLocalStorage(PRESCRIPTION_STARTED_KEY(prescriptionId), JSON.stringify(payload));
}

/**
 * Recupera o payload do evento prescription_started do localStorage.
 */
export function getPrescriptionStartedPayload(prescriptionId: number): PrescriptionStartedPayload | null {
  if (!amplitudeModule.isInitialized()) return null;
  const raw = safeGetFromLocalStorage(PRESCRIPTION_STARTED_KEY(prescriptionId));
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PrescriptionStartedPayload;
  } catch {
    return null;
  }
}

/**
 * Remove o payload do evento prescription_started do localStorage.
 * Deve ser chamado ao cancelar ou finalizar a prescrição.
 */
export function clearPrescriptionStartedPayload(prescriptionId: number): void {
  if (!amplitudeModule.isInitialized()) return;
  safeDeleteFromLocalStorage(PRESCRIPTION_STARTED_KEY(prescriptionId));
}

const PRESCRIPTION_COMPLETED_KEY = (prescriptionId: number) => `amplitude_prescription_completed_${prescriptionId}`;

/**
 * Persiste o payload do evento prescription_completed no localStorage.
 * Usado para acumular progressivamente os dados ao longo da jornada de prescrição.
 */
export function savePrescriptionCompletedPayload(prescriptionId: number, payload: PrescriptionCompletedPayload): void {
  if (!amplitudeModule.isInitialized()) return;
  safeSetInLocalStorage(PRESCRIPTION_COMPLETED_KEY(prescriptionId), JSON.stringify(payload));
}

/**
 * Recupera o payload do evento prescription_completed do localStorage.
 */
export function getPrescriptionCompletedPayload(prescriptionId: number): PrescriptionCompletedPayload | null {
  if (!amplitudeModule.isInitialized()) return null;
  const raw = safeGetFromLocalStorage(PRESCRIPTION_COMPLETED_KEY(prescriptionId));
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PrescriptionCompletedPayload;
  } catch {
    return null;
  }
}

/**
 * Remove o payload do evento prescription_completed do localStorage.
 * Deve ser chamado ao cancelar ou finalizar a prescrição.
 */
export function clearPrescriptionCompletedPayload(prescriptionId: number): void {
  if (!amplitudeModule.isInitialized()) return;
  safeDeleteFromLocalStorage(PRESCRIPTION_COMPLETED_KEY(prescriptionId));
}

export interface ConcomitanceOriginData {
  hasConcomitance: boolean;
  originId: number;
  originName: string;
}

const CONCOMITANCE_MAP_KEY = (prescriptionId: number) => `amplitude_concomitance_map_${prescriptionId}`;

/**
 * Salva os dados de origem de concomitância para um medicamento adicionado via concomitância.
 * Chave: idMedicamento do medicamento concomitante adicionado.
 */
export function saveConcomitanceOrigin(
  prescriptionId: number,
  concomitantMedicineId: number,
  data: ConcomitanceOriginData
): void {
  if (!amplitudeModule.isInitialized()) return;
  const raw = safeGetFromLocalStorage(CONCOMITANCE_MAP_KEY(prescriptionId));
  const map: Record<number, ConcomitanceOriginData> = raw ? JSON.parse(raw) : {};
  map[concomitantMedicineId] = data;
  safeSetInLocalStorage(CONCOMITANCE_MAP_KEY(prescriptionId), JSON.stringify(map));
}

/**
 * Recupera o mapa completo de concomitâncias para uma prescrição.
 */
export function getConcomitanceMap(
  prescriptionId: number
): Record<number, ConcomitanceOriginData> {
  if (!amplitudeModule.isInitialized()) return {};
  const raw = safeGetFromLocalStorage(CONCOMITANCE_MAP_KEY(prescriptionId));
  if (!raw) return {};
  try {
    return JSON.parse(raw) as Record<number, ConcomitanceOriginData>;
  } catch {
    return {};
  }
}

/**
 * Remove o mapa de concomitâncias do localStorage.
 */
export function clearConcomitanceMap(prescriptionId: number): void {
  if (!amplitudeModule.isInitialized()) return;
  safeDeleteFromLocalStorage(CONCOMITANCE_MAP_KEY(prescriptionId));
}

export interface SuggestedPosologyData {
  isSuggestedPosology: boolean;
  suggestedPosologyId: number | null;
}

const SUGGESTED_POSOLOGY_MAP_KEY = (prescriptionId: number) => `amplitude_suggested_posology_map_${prescriptionId}`;

/**
 * Salva os dados de posologia sugerida para um medicamento.
 * Chave: idMedicamento.
 */
export function saveSuggestedPosology(
  prescriptionId: number,
  medicineId: number,
  data: SuggestedPosologyData
): void {
  if (!amplitudeModule.isInitialized()) return;
  const raw = safeGetFromLocalStorage(SUGGESTED_POSOLOGY_MAP_KEY(prescriptionId));
  const map: Record<number, SuggestedPosologyData> = raw ? JSON.parse(raw) : {};
  map[medicineId] = data;
  safeSetInLocalStorage(SUGGESTED_POSOLOGY_MAP_KEY(prescriptionId), JSON.stringify(map));
}

/**
 * Recupera o mapa de posologias sugeridas para uma prescrição.
 */
export function getSuggestedPosologyMap(
  prescriptionId: number
): Record<number, SuggestedPosologyData> {
  if (!amplitudeModule.isInitialized()) return {};
  const raw = safeGetFromLocalStorage(SUGGESTED_POSOLOGY_MAP_KEY(prescriptionId));
  if (!raw) return {};
  try {
    return JSON.parse(raw) as Record<number, SuggestedPosologyData>;
  } catch {
    return {};
  }
}

/**
 * Remove o mapa de posologias sugeridas do localStorage.
 */
export function clearSuggestedPosologyMap(prescriptionId: number): void {
  if (!amplitudeModule.isInitialized()) return;
  safeDeleteFromLocalStorage(SUGGESTED_POSOLOGY_MAP_KEY(prescriptionId));
}

const PROTOCOL_MAP_KEY = (prescriptionId: number) => `amplitude_protocol_map_${prescriptionId}`;

/**
 * Salva os dados de um modelo de prescrição (protocolo) utilizado na composição da prescrição.
 * Chave: id do modelo.
 */
export function saveProtocolData(
  prescriptionId: number,
  protocol: PrescriptionCompletedProtocol
): void {
  if (!amplitudeModule.isInitialized()) return;
  const raw = safeGetFromLocalStorage(PROTOCOL_MAP_KEY(prescriptionId));
  const map: Record<number, PrescriptionCompletedProtocol> = raw ? JSON.parse(raw) : {};
  map[protocol.id] = protocol;
  safeSetInLocalStorage(PROTOCOL_MAP_KEY(prescriptionId), JSON.stringify(map));
}

/**
 * Recupera o mapa de protocolos (modelos de prescrição) para uma prescrição.
 */
export function getProtocolMap(
  prescriptionId: number
): Record<number, PrescriptionCompletedProtocol> {
  if (!amplitudeModule.isInitialized()) return {};
  const raw = safeGetFromLocalStorage(PROTOCOL_MAP_KEY(prescriptionId));
  if (!raw) return {};
  try {
    return JSON.parse(raw) as Record<number, PrescriptionCompletedProtocol>;
  } catch {
    return {};
  }
}

/**
 * Remove o mapa de protocolos do localStorage.
 */
export function clearProtocolMap(prescriptionId: number): void {
  if (!amplitudeModule.isInitialized()) return;
  safeDeleteFromLocalStorage(PROTOCOL_MAP_KEY(prescriptionId));
}
